import React, { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import "./MapplsAutocomplete.css";

const MapplsAutocomplete = ({
  value,
  onChange,
  onSelect,
  label = "Company",
  variant = "filled",
  className = "",
  countryCode = "",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Check if Mappls SDK is already loaded
    if (window.mappls) {
      setIsLoaded(true);
      return;
    }

    // Load Mappls SDK script if not already loaded
    const script = document.createElement("script");
    script.src =
      "https://apis.mappls.com/advancedmaps/api/b549c76376745c9166019ff7da79a72d/map_sdk?layer=vector&v=3.0&callback=initMapplsAutocomplete";
    script.async = true;

    window.initMapplsAutocomplete = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error("Failed to load Mappls SDK for autocomplete");
    };

    document.body.appendChild(script);

    return () => {
      delete window.initMapplsAutocomplete;
    };
  }, []);

  const fetchSuggestions = async (query) => {
    if (!query || query.length < 3 || !isLoaded) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    try {
      const url = `https://atlas.mappls.com/api/places/search/json?query=${encodeURIComponent(
        query
      )}&access_token=b549c76376745c9166019ff7da79a72d`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.suggestedLocations && data.suggestedLocations.length > 0) {
        setSuggestions(data.suggestedLocations);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout for debounced search
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    const selectedValue = suggestion.placeName || suggestion.placeAddress;
    onChange(selectedValue);
    onSelect(selectedValue, suggestion);
    setSuggestions([]);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click event to fire
    setTimeout(() => {
      setSuggestions([]);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="mappls-autocomplete-container">
      <TextField
        ref={inputRef}
        id="mappls-autocomplete-input"
        label={label}
        variant={variant}
        className={className}
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        style={{ width: "100%" }}
        InputLabelProps={{
          shrink: Boolean(value),
        }}
      />
      {(loading || suggestions.length > 0) && (
        <div className="mappls-suggestions-container">
          {loading && <div className="mappls-loading">Loading...</div>}
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="mappls-suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="mappls-suggestion-name">
                {suggestion.placeName}
              </div>
              {suggestion.placeAddress && (
                <div className="mappls-suggestion-address">
                  {suggestion.placeAddress}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapplsAutocomplete;
